import { memo, useState, useEffect, useMemo, useCallback } from 'react';
import { isNil } from 'lodash';
// Import Types
import type { SegmentCompositeConfiguration, SegmentById } from 'types';
//Import UI Components
import { BusyLoader, Tooltip } from '@geneui/components';
// Import Components
import CustomTabs from 'components/common/CustomTabs';
import CompositeSegmentInfoTab from './CompositeSegmentInfoTab';
// Import Services
import { SegmentHttpService } from 'services/http';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import Context
import { useVersionHistoryContext } from 'components/VersionHistory';
// Import Styles
import 'assets/scss/compositeSegmentInfo.scss';

interface Props {
    compositeConfiguration?: Nullable<SegmentCompositeConfiguration>;
}

const CompositeSegmentInfo: React.FC<Props> = ({ compositeConfiguration }) => {
    const { doGetRequest } = useRequest();
    const { pointInTime } = useVersionHistoryContext();

    const [isLoading, setIsLoading] = useState(false);
    const [childSegments, setChildSegments] = useState<SegmentById[]>();
    const [tabActiveKey, setTabActiveKey] = useState(0);

    const { getSegmentById } = useCancelableRequests({
        getSegmentById: SegmentHttpService.getSegmentById,
    });

    const tabs = useMemo(() => {
        return isNil(childSegments)
            ? []
            : childSegments.map((childSegment, index) => {
                  return {
                      tabKey: index,
                      tabTitle: (
                          <Tooltip text={childSegment.Name}>
                              <div>
                                  <p className="ellipsis-text">{childSegment.Name}</p>
                              </div>
                          </Tooltip>
                      ),
                      tabContent: <CompositeSegmentInfoTab key={index} childSegment={childSegment} />,
                  };
              });
    }, [childSegments]);

    const tabChangeHandler = useCallback((tabIndex) => {
        setTabActiveKey(tabIndex);
    }, []);

    const getSegments = (segmentIds) => {
        return segmentIds.map((element) => {
            return doGetRequest(getSegmentById.request, { queryString: { segmentId: element, pointInTime } });
        });
    };

    const mapSegmentsInfo = (responses) => {
        // for promise all need has error but case dows not cover
        let hasError = false;

        const segmentsData = responses.map((response) => {
            if (isNil(response)) return response;

            const { HasError, Data } = response;

            if (!HasError) {
                return Data;
            }

            hasError = true;
            return null;
        });

        return { Data: segmentsData, HasError: hasError };
    };

    const init = () => {
        setIsLoading(true);

        Promise.all(getSegments(compositeConfiguration?.SegmentIds ?? [])).then((responses) => {
            const { Data: segmentsData, HasError: hasError } = mapSegmentsInfo(responses);

            setChildSegments(segmentsData);
            setIsLoading(hasError);
        });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    return (
        <>
            <BusyLoader
                isBusy={isLoading || isNil(childSegments)}
                type="spinner"
                spinnerSize="medium"
                className="crm-justify-content-center"
            >
                <>
                    {!isLoading && !isNil(childSegments) && (
                        <CustomTabs
                            activeTabKey={tabActiveKey}
                            tabsConfig={tabs}
                            tabChangeHandler={tabChangeHandler}
                            customCssClassNames="crm-campaign-report-block crm-composite-segment-info-tabs"
                        />
                    )}
                </>
            </BusyLoader>
        </>
    );
};

export default memo(CompositeSegmentInfo);
